/**
 * OCT28 RECOVERY #1 (founder audit 2026-11-02) — the VIN, plate and issuing state a ticket card shows.
 *
 * Every vehicles.vin / license_plate in production is an encrypted envelope (`v1:` / `v2:`), so the Dayboard's own
 * row select can never show them. This reads the identity, decrypted by vehicle-read, KEYED BY VEHICLE ID — so every
 * board path (first load, single-ticket refresh, realtime INSERT/UPDATE), the Command Center and the Birdseye sheet
 * show the same value for existing tickets and rollover continuations alike (they share their vehicle row).
 *
 * The cards of one render ask in the same tick; those asks are coalesced into ONE request per shop.
 * Only viewers of canViewVehicleIdentity ask at all; the server re-checks the role.
 */
import { useQuery } from '@tanstack/react-query';
import { fetchVehicleIdentities, type VehicleIdentity } from '@/lib/api/vehicleApi';

type Pending = { ids: Set<string>; waiters: Array<{ id: string; resolve: (v: VehicleIdentity | null) => void; reject: (e: unknown) => void }> };
const pendingByShop = new Map<string, Pending>();
const MAX_BATCH = 300;

function flush(shopId: string) {
  const p = pendingByShop.get(shopId);
  pendingByShop.delete(shopId);
  if (!p) return;
  const ids = [...p.ids];
  for (let i = 0; i < ids.length; i += MAX_BATCH) {
    const chunk = ids.slice(i, i + MAX_BATCH);
    const mine = p.waiters.filter((w) => chunk.includes(w.id));
    fetchVehicleIdentities(shopId, chunk).then(
      (rows) => {
        const byId = new Map(rows.map((r) => [r.id, r]));
        for (const w of mine) w.resolve(byId.get(w.id) ?? null);
      },
      (e) => { for (const w of mine) w.reject(e); },
    );
  }
}

/** Exported for the unit test: one request for every id asked in the same tick. */
export function loadVehicleIdentity(shopId: string, vehicleId: string): Promise<VehicleIdentity | null> {
  return new Promise((resolve, reject) => {
    let p = pendingByShop.get(shopId);
    if (!p) {
      p = { ids: new Set(), waiters: [] };
      pendingByShop.set(shopId, p);
      setTimeout(() => flush(shopId), 0);
    }
    p.ids.add(vehicleId);
    p.waiters.push({ id: vehicleId, resolve, reject });
  });
}

export function useVehicleIdentity(shopId: string | null | undefined, vehicleId: string | null | undefined, enabled: boolean) {
  const on = Boolean(enabled && shopId && vehicleId);
  const q = useQuery({
    queryKey: ['vehicle-identity', shopId, vehicleId],
    queryFn: () => loadVehicleIdentity(shopId as string, vehicleId as string),
    enabled: on,
    // Every read is a recorded sensitive access (one log row per batch). A VIN / plate rarely changes, so a board
    // re-reads only when a vehicle first appears or after 30 minutes — never on window focus or reconnect.
    staleTime: 30 * 60_000,
    gcTime: 60 * 60_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 1,
  });
  return { identity: on ? q.data ?? null : null, loading: on && q.isLoading, failed: on && q.isError };
}
